import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, Users, GraduationCap, MapPin, Layers, Globe2,
  Building2, Navigation, ChevronRight, Crosshair, Maximize2,
  School as SchoolIcon, Filter, TrendingUp, AlertTriangle,
  CheckCircle2, BarChart3, Activity, Eye, ShieldCheck,
} from "lucide-react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";
import { MAP_STYLES } from "@/lib/map-theme";
import { useCatchments, useSchools } from "./hooks";
import { searchAll } from "./service";
import { CatchmentModal } from "./catchment-modal";
import { SchoolModal } from "./school-modal";
import { getSchoolMarkerSVG, getClusterSVG, getUnassignedMarkerSVG, SCHOOL_LEVEL_COLORS } from "./markers";
import { getCatchmentFinance, getFinancialColor, fmtUSD, fmtNum, fmtPct } from "./finance";
import type {
  Catchment, CatchmentSchool, MapFilters, MapLayers, MapType,
  SearchResult, EducationLevel, Ownership, BoardingType,
  FinancialMode, FinancialLayer,
} from "./types";

declare global {
  interface Window {
    google?: any;
  }
}

const C = {
  forest: "#14532D",
  forestDeep: "#0C3320",
  forestMid: "#1E7A45",
  forestTint: "#E8F1EA",
  terracotta: "#B33A1A",
  mustard: "#C4A882",
  sky: "#0284c7",
  amber: "#d97706",
  emerald: "#059669",
  rose: "#e11d48",
  text: "#1a2e23",
  textMuted: "#5a7a6a",
  textLight: "#8aaa9a",
  bg: "#F6FAF8",
  card: "#FFFFFF",
  border: "#E0EDE7",
};

const CATCHMENT_COLORS = [
  "#14532D", "#1E7A45", "#0284c7", "#d97706", "#B33A1A",
  "#7c3aed", "#059669", "#e11d48",
];

const OWNERSHIP_LABELS: Record<string, string> = {
  government: "Government", private: "Private", community: "Community", other: "Other",
};

const BOARDING_LABELS: Record<string, string> = {
  day: "Day", boarding: "Boarding", mixed: "Day & Boarding",
};

const FINANCIAL_LAYERS: { key: FinancialLayer; label: string }[] = [
  { key: "volume", label: "Financial Volume" },
  { key: "collections", label: "Collections" },
  { key: "outstanding", label: "Outstanding" },
  { key: "population", label: "Student Population" },
  { key: "penetration", label: "BRECCS Penetration" },
  { key: "opportunity", label: "Opportunity" },
];

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, accent }: {
  icon: any; label: string; value: string; sub?: string; accent: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative overflow-hidden rounded-xl border bg-white p-4"
      style={{ borderColor: C.border }}
    >
      <div className="absolute -right-3 -top-3 h-16 w-16 rounded-full opacity-[0.06]" style={{ background: accent }} />
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: `${accent}15`, color: accent }}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: C.textMuted, opacity: 0.7 }}>{label}</p>
          <p className="text-xl font-bold leading-tight" style={{ color: C.text }}>{value}</p>
        </div>
      </div>
      {sub && <p className="mt-1.5 text-[11px]" style={{ color: C.textMuted }}>{sub}</p>}
    </motion.div>
  );
}

// ── Filter pill ─────────────────────────────────────────────────────────────
function FilterPill({ active, onClick, children, color }: {
  active: boolean; onClick: () => void; children: React.ReactNode; color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition"
      style={{
        background: active ? (color ?? C.forest) : "transparent",
        color: active ? "#fff" : C.textMuted,
        border: `1px solid ${active ? (color ?? C.forest) : C.border}`,
      }}
    >
      {children}
    </button>
  );
}

// ── Search bar ──────────────────────────────────────────────────────────────
function SearchBar({ catchments, schools, onSelect }: {
  catchments: Catchment[];
  schools: CatchmentSchool[];
  onSelect: (result: SearchResult) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => searchAll(catchments, schools, q), [catchments, schools, q]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: C.textMuted }} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Search Zimbabwe schools, catchments, districts…"
          className="w-full rounded-xl py-2.5 pl-10 pr-9 text-sm outline-none transition"
          style={{ border: `1px solid ${C.border}`, background: C.card, color: C.text }}
        />
        {q && (
          <button onClick={() => { setQ(""); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: C.textMuted }}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-xl border bg-white shadow-xl"
            style={{ borderColor: C.border }}
          >
            {results.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => { onSelect(r); setQ(""); setOpen(false); }}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-gray-50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{
                  background: r.type === "school" ? `${C.sky}15` : r.type === "catchment" ? `${C.forest}15` : `${C.amber}15`,
                }}>
                  {r.type === "school" ? <SchoolIcon className="h-4 w-4" style={{ color: C.sky }} />
                    : r.type === "catchment" ? <Globe2 className="h-4 w-4" style={{ color: C.forest }} />
                    : <MapPin className="h-4 w-4" style={{ color: C.amber }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium" style={{ color: C.text }}>{r.label}</div>
                  <div className="truncate text-xs" style={{ color: C.textMuted }}>{r.sublabel}</div>
                </div>
                <span className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase" style={{
                  background: `${C.textMuted}15`, color: C.textMuted,
                }}>{r.type}</span>
                {r.type === "catchment" && r.schoolCount != null && r.schoolCount > 0 && (
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{
                    background: `${C.forest}12`, color: C.forest,
                  }}>{r.schoolCount} school{r.schoolCount === 1 ? "" : "s"}</span>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function SchoolCatchmentMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const gmapRef = useRef<any>(null);
  const polygonsRef = useRef<any[]>([]);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = useState(7);

  const [layers, setLayers] = useState<MapLayers>({
    catchments: true,
    schools: true,
    districts: false,
    regions: false,
  });

  const [filters, setFilters] = useState<MapFilters>({
    schoolType: "all",
    ownership: "all",
    boardingType: "all",
    studentRange: "all",
    catchmentStatus: "all",
  });

  const [mapType, setMapType] = useState<MapType>("roadmap");
  const [showFilters, setShowFilters] = useState(false);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [selSchool, setSelSchool] = useState<CatchmentSchool | null>(null);
  const [selCatchment, setSelCatchment] = useState<Catchment | null>(null);
  const [hoveredCatchment, setHoveredCatchment] = useState<string | null>(null);
  const [financialMode, setFinancialMode] = useState<FinancialMode>("geographic");
  const [financialLayer, setFinancialLayer] = useState<FinancialLayer>("volume");

  const { data: catchments = [], isLoading: catchLoading } = useCatchments();
  const { data: schools = [], isLoading: schoolLoading } = useSchools();

  const filteredSchools = useMemo(() => schools.filter((s) => {
    if (filters.schoolType !== "all" && s.education_level !== filters.schoolType) return false;
    if (filters.ownership !== "all" && s.ownership !== filters.ownership) return false;
    if (filters.boardingType !== "all" && s.boarding_type !== filters.boardingType) return false;
    if (filters.studentRange !== "all") {
      const n = s.student_count;
      switch (filters.studentRange) {
        case "0-500": if (n >= 500) return false; break;
        case "500-1000": if (n < 500 || n >= 1000) return false; break;
        case "1000-2000": if (n < 1000 || n >= 2000) return false; break;
        case "2000+": if (n < 2000) return false; break;
      }
    }
    if (showUnassigned && s.catchment_id != null) return false;
    return true;
  }), [schools, filters, showUnassigned]);

  const filteredCatchments = useMemo(() => {
    if (filters.catchmentStatus !== "all") {
      return catchments.filter((c) => c.status === filters.catchmentStatus);
    }
    return catchments;
  }, [catchments, filters.catchmentStatus]);

  // Compute catchment finance for financial mode
  const catchmentFinanceMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getCatchmentFinance>>();
    for (const c of catchments) {
      const cs = schools.filter(s => s.catchment_id === c.id);
      map.set(c.id, getCatchmentFinance(cs));
    }
    return map;
  }, [catchments, schools]);

  const stats = useMemo(() => {
    const totalSchools = schools.length;
    const totalStudents = schools.reduce((sum, s) => sum + s.student_count, 0);
    const totalTeachers = schools.reduce((sum, s) => sum + s.teacher_count, 0);
    const activeCatchments = catchments.filter((c) => c.status === "active").length;
    const gpsLocated = schools.filter(s => s.latitude != null && s.longitude != null).length;
    const catchmentAssigned = schools.filter(s => s.catchment_id != null).length;
    const unassigned = schools.filter(s => s.catchment_id == null).length;
    return { totalSchools, totalStudents, totalTeachers, activeCatchments, gpsLocated, catchmentAssigned, unassigned };
  }, [schools, catchments]);

  // Init map
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !mapRef.current || !window.google) return;
      gmapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: -17.8322, lng: 31.0501 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: MAP_STYLES["education-atlas"].styles,
        backgroundColor: "#F0F9FF",
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
      gmapRef.current.addListener("zoom_changed", () => {
        setCurrentZoom(gmapRef.current.getZoom());
      });
      setMapReady(true);
    }).catch((e) => setErr(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, []);

  // Map type toggle
  useEffect(() => {
    if (!gmapRef.current || !window.google) return;
    const typeId = mapType === "satellite" ? window.google.maps.MapTypeId.SATELLITE
      : mapType === "hybrid" ? window.google.maps.MapTypeId.HYBRID
      : mapType === "terrain" ? window.google.maps.MapTypeId.TERRAIN
      : window.google.maps.MapTypeId.ROADMAP;
    gmapRef.current.setMapTypeId(typeId);
  }, [mapType, mapReady]);

  // Render catchment polygons
  useEffect(() => {
    if (!mapReady || !window.google) return;
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];

    if (!layers.catchments) return;

    // Determine max financial value for color scaling
    let maxFin = 0;
    if (financialMode === "financial") {
      for (const c of filteredCatchments) {
        const fin = catchmentFinanceMap.get(c.id);
        if (fin) {
          const val = financialLayer === "volume" ? fin.annualFees
            : financialLayer === "collections" ? fin.collected
            : financialLayer === "outstanding" ? fin.outstanding
            : financialLayer === "population" ? c.student_count
            : financialLayer === "penetration" ? fin.breccsVolume
            : fin.opportunity;
          if (val > maxFin) maxFin = val;
        }
      }
    }

    for (let i = 0; i < filteredCatchments.length; i++) {
      const c = filteredCatchments[i];
      if (!c.geometry) continue;

      let color: string;
      if (financialMode === "financial") {
        const fin = catchmentFinanceMap.get(c.id);
        const val = fin ? (financialLayer === "volume" ? fin.annualFees
          : financialLayer === "collections" ? fin.collected
          : financialLayer === "outstanding" ? fin.outstanding
          : financialLayer === "population" ? c.student_count
          : financialLayer === "penetration" ? fin.breccsVolume
          : fin.opportunity) : 0;
        color = getFinancialColor(val, maxFin, financialLayer);
      } else {
        color = CATCHMENT_COLORS[i % CATCHMENT_COLORS.length];
      }

      const paths = geoJsonToPaths(c.geometry);
      if (!paths.length) continue;

      const isSelected = selCatchment?.id === c.id;
      const isHovered = hoveredCatchment === c.id;

      const polygon = new window.google.maps.Polygon({
        paths,
        map: gmapRef.current,
        strokeColor: color,
        strokeOpacity: isSelected ? 1 : 0.9,
        strokeWeight: isSelected ? 3.5 : 2.5,
        fillColor: color,
        fillOpacity: isSelected ? 0.35 : isHovered ? 0.28 : financialMode === "financial" ? 0.42 : 0.22,
        clickable: true,
      });
      polygon.addListener("click", () => {
        setSelSchool(null);
        setSelCatchment(c);
        fitBoundsToCatchment(c);
      });

      // Financial tooltip
      const fin = catchmentFinanceMap.get(c.id);
      polygon.addListener("mouseover", (e: any) => {
        setHoveredCatchment(c.id);
        if (financialMode === "financial" && fin) {
          const content = `
            <div style="font-family:Inter,sans-serif;padding:4px 2px;min-width:180px">
              <div style="font-weight:700;font-size:13px;color:${C.text};margin-bottom:4px">${c.name}</div>
              <div style="font-size:11px;color:${C.textMuted}">${c.school_count} Schools · ${fmtNum(c.student_count)} Students</div>
              <div style="margin-top:6px;font-size:11px;color:${C.textMuted}">Annual Economy: <b style="color:${C.forest}">${fmtUSD(fin.annualFees)}</b></div>
              <div style="font-size:11px;color:${C.textMuted}">Collection: <b style="color:${C.emerald}">${fmtPct(fin.collectionRate)}</b></div>
              <div style="font-size:11px;color:${C.textMuted}">Outstanding: <b style="color:${C.rose}">${fmtUSD(fin.outstanding)}</b></div>
              <div style="margin-top:4px;font-size:10px;color:${C.textLight}">Click to explore</div>
            </div>`;
          if (infoWindowRef.current && e.latLng) {
            infoWindowRef.current.setContent(content);
            infoWindowRef.current.setPosition(e.latLng);
            infoWindowRef.current.open(gmapRef.current);
          }
        } else if (financialMode === "geographic") {
          const content = `
            <div style="font-family:Inter,sans-serif;padding:4px 2px;min-width:160px">
              <div style="font-weight:700;font-size:13px;color:${C.text};margin-bottom:4px">${c.name}</div>
              <div style="font-size:11px;color:${C.textMuted}">${c.school_count} School${c.school_count === 1 ? "" : "s"} · ${fmtNum(c.student_count)} Students</div>
              <div style="font-size:11px;color:${C.textMuted};margin-top:2px">${c.district ?? "—"} · ${c.region ?? "Zimbabwe"}</div>
              <div style="margin-top:4px;font-size:10px;color:${C.textLight}">Click to explore</div>
            </div>`;
          if (infoWindowRef.current && e.latLng) {
            infoWindowRef.current.setContent(content);
            infoWindowRef.current.setPosition(e.latLng);
            infoWindowRef.current.open(gmapRef.current);
          }
        }
      });
      polygon.addListener("mouseout", () => {
        setHoveredCatchment(null);
        if (infoWindowRef.current) infoWindowRef.current.close();
      });
      polygonsRef.current.push(polygon);
    }
  }, [mapReady, filteredCatchments, layers.catchments, hoveredCatchment, selCatchment, financialMode, financialLayer, catchmentFinanceMap]);

  // Render school markers with zoom-based sizing and clustering
  useEffect(() => {
    if (!mapReady || !window.google) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (!layers.schools) return;

    const zoom = gmapRef.current?.getZoom() ?? 7;
    const markerSize: "sm" | "md" | "lg" = zoom >= 12 ? "lg" : zoom >= 9 ? "md" : "sm";

    // At low zoom, cluster by district
    if (zoom < 9) {
      const districtClusters = new Map<string, CatchmentSchool[]>();
      for (const s of filteredSchools) {
        if (s.latitude == null || s.longitude == null) continue;
        const key = s.district ?? "Unknown";
        if (!districtClusters.has(key)) districtClusters.set(key, []);
        districtClusters.get(key)!.push(s);
      }
      for (const [, clusterSchools] of districtClusters) {
        if (clusterSchools.length === 0) continue;
        const avgLat = clusterSchools.reduce((a, s) => a + (s.latitude ?? 0), 0) / clusterSchools.length;
        const avgLng = clusterSchools.reduce((a, s) => a + (s.longitude ?? 0), 0) / clusterSchools.length;
        const count = clusterSchools.length;
        const marker = new window.google.maps.Marker({
          position: { lat: avgLat, lng: avgLng },
          map: gmapRef.current,
          title: `${count} schools`,
          icon: {
            url: getClusterSVG(count),
            scaledSize: new window.google.maps.Size(52, 52),
            anchor: new window.google.maps.Point(26, 26),
          },
        });
        marker.addListener("click", () => {
          gmapRef.current?.panTo({ lat: avgLat, lng: avgLng });
          gmapRef.current?.setZoom(10);
        });
        markersRef.current.push(marker);
      }
    } else {
      // Show individual markers at higher zoom
      for (const s of filteredSchools) {
        if (s.latitude == null || s.longitude == null) continue;
        const isUnassigned = s.catchment_id == null;
        const isSelected = selSchool?.id === s.id;
        const icon = isUnassigned
          ? getUnassignedMarkerSVG()
          : getSchoolMarkerSVG(s.education_level, s.location_status, isSelected, markerSize);
        const dim = markerSize === "sm" ? { w: 28, h: 36 } : markerSize === "lg" ? { w: 44, h: 56 } : { w: 36, h: 46 };

        const marker = new window.google.maps.Marker({
          position: { lat: s.latitude, lng: s.longitude },
          map: gmapRef.current,
          title: s.name,
          icon: {
            url: icon,
            scaledSize: new window.google.maps.Size(dim.w, dim.h),
            anchor: new window.google.maps.Point(dim.w / 2, dim.h),
          },
          animation: window.google.maps.Animation.DROP,
          zIndex: isSelected ? 1000 : 1,
        });

        // Hover info
        const catchmentName = catchments.find(c => c.id === s.catchment_id)?.name ?? "No catchment";
        const content = `
          <div style="font-family:Inter,sans-serif;padding:4px 2px;min-width:200px">
            <div style="font-weight:700;font-size:13px;color:${C.text};margin-bottom:2px">${s.name}</div>
            <div style="font-size:11px;color:${C.textMuted}">${s.education_level ?? "school"} · ${OWNERSHIP_LABELS[s.ownership ?? "other"] ?? "—"}</div>
            <div style="font-size:11px;color:${C.textMuted};margin-top:2px">${fmtNum(s.student_count)} Students</div>
            <div style="font-size:11px;color:${C.forest};margin-top:2px">${catchmentName}</div>
          </div>`;
        marker.addListener("click", () => {
          setSelCatchment(null);
          setSelSchool(s);
          if (infoWindowRef.current) {
            infoWindowRef.current.setContent(content);
            infoWindowRef.current.open(gmapRef.current, marker);
          }
        });
        markersRef.current.push(marker);
      }
    }
  }, [mapReady, filteredSchools, layers.schools, currentZoom, selSchool, catchments]);

  function geoJsonToPaths(geom: any): any[] {
    if (!geom) return [];
    const paths: any[] = [];
    let coords: number[][][] = [];
    if (geom.type === "Polygon") {
      coords = geom.coordinates;
    } else if (geom.type === "MultiPolygon") {
      for (const poly of geom.coordinates) coords.push(...poly);
    }
    for (const ring of coords) {
      paths.push(ring.map(([lng, lat]: [number, number]) => ({ lat, lng })));
    }
    return paths;
  }

  function fitBoundsToCatchment(c: Catchment) {
    if (!c.geometry || !gmapRef.current || !window.google) return;
    const paths = geoJsonToPaths(c.geometry);
    if (!paths.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    for (const path of paths) for (const p of path) bounds.extend(p);
    gmapRef.current.fitBounds(bounds, 60);
  }

  const handleSearchSelect = useCallback((result: SearchResult) => {
    if (result.type === "school" && result.lat && result.lng) {
      gmapRef.current?.panTo({ lat: result.lat, lng: result.lng });
      gmapRef.current?.setZoom(14);
      const school = schools.find((s) => s.id === result.id);
      if (school) { setSelCatchment(null); setSelSchool(school); }
    } else if (result.type === "catchment" && result.catchmentId) {
      const c = catchments.find((c) => c.id === result.catchmentId);
      if (c) { setSelSchool(null); setSelCatchment(c); fitBoundsToCatchment(c); }
    } else if (result.type === "district" && result.lat && result.lng) {
      gmapRef.current?.panTo({ lat: result.lat, lng: result.lng });
      gmapRef.current?.setZoom(11);
    }
  }, [schools, catchments]);

  if (err) {
    return (
      <div className="grid h-[420px] place-items-center rounded-xl border border-dashed text-sm" style={{ borderColor: C.border, color: C.textMuted }}>
        Map unavailable: {err}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Hero header ── */}
      <div className="relative overflow-hidden rounded-2xl" style={{ background: `linear-gradient(135deg, ${C.forestDeep}, ${C.forest})` }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, ${C.forestMid} 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${C.emerald} 0%, transparent 40%)`,
        }} />
        <div className="absolute right-0 top-0 h-32 w-32 opacity-[0.08]">
          <Globe2 className="h-full w-full text-white" />
        </div>
        <div className="relative p-5 sm:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "rgba(255,255,255,0.1)" }}>
                  <Globe2 className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold tracking-tight text-white">BRECCS Catchment Intelligence</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Zimbabwe National School Catchment Map</div>
                </div>
              </div>
              <p className="mt-1.5 max-w-xl text-sm text-white/50">
                National school catchment map — schools, catchment areas, and education networks across Zimbabwe, visualised in real time.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(["roadmap", "satellite", "hybrid", "terrain"] as MapType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setMapType(t)}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition"
                  style={{
                    background: mapType === t ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.05)",
                    color: mapType === t ? "#fff" : "rgba(255,255,255,0.5)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* National coverage stats */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{fmtNum(stats.totalSchools)}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">Schools</p>
            </div>
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{fmtNum(stats.gpsLocated)}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">GPS Located</p>
            </div>
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{stats.totalSchools > 0 ? fmtPct(stats.gpsLocated / stats.totalSchools) : "—"}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">GPS Coverage</p>
            </div>
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{fmtNum(stats.catchmentAssigned)}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">Catchment Assigned</p>
            </div>
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{stats.totalSchools > 0 ? fmtPct(stats.catchmentAssigned / stats.totalSchools) : "—"}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">Assignment Rate</p>
            </div>
            <div className="rounded-lg bg-white/5 p-3">
              <p className="text-lg font-bold text-white">{fmtNum(stats.activeCatchments)}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">Catchments</p>
            </div>
            <div className="rounded-lg" style={{ background: stats.unassigned > 0 ? "rgba(225,29,72,0.15)" : "rgba(255,255,255,0.05)" }}>
              <p className="text-lg font-bold" style={{ color: stats.unassigned > 0 ? "#fca5a5" : "#fff" }}>{fmtNum(stats.unassigned)}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">Unassigned</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={SchoolIcon} label="Schools" value={fmtNum(stats.totalSchools)} sub={`${stats.gpsLocated} GPS located`} accent={C.forest} />
        <StatCard icon={Users} label="Students" value={fmtNum(stats.totalStudents)} sub="across all catchments" accent={C.emerald} />
        <StatCard icon={GraduationCap} label="Teachers" value={fmtNum(stats.totalTeachers)} sub="in network" accent={C.sky} />
        <StatCard icon={Globe2} label="Catchments" value={fmtNum(stats.activeCatchments)} sub={`${stats.unassigned} unassigned schools`} accent={C.amber} />
      </div>

      {/* ── Search + controls bar ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[280px] flex-1">
          <SearchBar catchments={catchments} schools={schools} onSelect={handleSearchSelect} />
        </div>

        {/* Financial mode toggle */}
        <div className="flex items-center rounded-xl border" style={{ borderColor: C.border, background: C.card }}>
          <button
            onClick={() => setFinancialMode("geographic")}
            className="flex items-center gap-1.5 rounded-l-xl px-3 py-2.5 text-xs font-medium transition"
            style={{
              background: financialMode === "geographic" ? C.forest : "transparent",
              color: financialMode === "geographic" ? "white" : C.textMuted,
            }}
          >
            <MapPin className="h-3.5 w-3.5" /> Geographic
          </button>
          <button
            onClick={() => setFinancialMode("financial")}
            className="flex items-center gap-1.5 rounded-r-xl px-3 py-2.5 text-xs font-medium transition"
            style={{
              background: financialMode === "financial" ? C.forest : "transparent",
              color: financialMode === "financial" ? "white" : C.textMuted,
            }}
          >
            <TrendingUp className="h-3.5 w-3.5" /> Financial
          </button>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition"
          style={{
            borderColor: showFilters ? C.forest : C.border,
            background: showFilters ? `${C.forest}10` : "white",
            color: showFilters ? C.forest : C.textMuted,
          }}
        >
          <Filter className="h-4 w-4" /> Filters
        </button>
      </div>

      {/* ── Financial layer selector ── */}
      {financialMode === "financial" && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: C.textMuted }}>
            <BarChart3 className="h-3.5 w-3.5" /> Financial Layer:
          </div>
          {FINANCIAL_LAYERS.map(l => (
            <button
              key={l.key}
              onClick={() => setFinancialLayer(l.key)}
              className="rounded-lg px-2.5 py-1 text-xs font-medium transition"
              style={{
                background: financialLayer === l.key ? C.forest : "transparent",
                color: financialLayer === l.key ? "white" : C.textMuted,
                border: `1px solid ${financialLayer === l.key ? C.forest : C.border}`,
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Filter panel ── */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="rounded-xl border bg-white p-4" style={{ borderColor: C.border }}>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>School Type</div>
                  <div className="flex flex-wrap gap-1.5">
                    <FilterPill active={filters.schoolType === "all"} onClick={() => setFilters({ ...filters, schoolType: "all" })}>All</FilterPill>
                    {(["nursery", "primary", "secondary", "tertiary", "vocational"] as EducationLevel[]).map((t) => (
                      <FilterPill key={t} active={filters.schoolType === t} onClick={() => setFilters({ ...filters, schoolType: t })} color={SCHOOL_LEVEL_COLORS[t]}>
                        <span className="capitalize">{t}</span>
                      </FilterPill>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Ownership</div>
                  <div className="flex flex-wrap gap-1.5">
                    <FilterPill active={filters.ownership === "all"} onClick={() => setFilters({ ...filters, ownership: "all" })}>All</FilterPill>
                    {(["government", "private", "community"] as Ownership[]).map((o) => (
                      <FilterPill key={o} active={filters.ownership === o} onClick={() => setFilters({ ...filters, ownership: o })}>
                        {OWNERSHIP_LABELS[o]}
                      </FilterPill>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Boarding</div>
                  <div className="flex flex-wrap gap-1.5">
                    <FilterPill active={filters.boardingType === "all"} onClick={() => setFilters({ ...filters, boardingType: "all" })}>All</FilterPill>
                    {(["day", "boarding", "mixed"] as BoardingType[]).map((b) => (
                      <FilterPill key={b} active={filters.boardingType === b} onClick={() => setFilters({ ...filters, boardingType: b })}>
                        {BOARDING_LABELS[b]}
                      </FilterPill>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Students</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(["all", "0-500", "500-1000", "1000-2000", "2000+"] as const).map((r) => (
                      <FilterPill key={r} active={filters.studentRange === r} onClick={() => setFilters({ ...filters, studentRange: r })}>
                        {r === "all" ? "All" : r}
                      </FilterPill>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-t pt-3" style={{ borderColor: C.border }}>
                <button
                  onClick={() => setShowUnassigned(!showUnassigned)}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition"
                  style={{
                    borderColor: showUnassigned ? C.rose : C.border,
                    background: showUnassigned ? `${C.rose}10` : "white",
                    color: showUnassigned ? C.rose : C.textMuted,
                  }}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {showUnassigned ? "Showing unassigned schools only" : "Show schools without catchments"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Layer toggles ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4" style={{ color: C.textMuted }} />
          <span className="text-xs font-medium uppercase tracking-wide" style={{ color: C.textMuted }}>Layers</span>
        </div>
        {([
          { key: "catchments" as const, label: "Catchments", count: filteredCatchments.length, color: C.forest },
          { key: "schools" as const, label: "Schools", count: filteredSchools.length, color: C.sky },
        ]).map((l) => (
          <button
            key={l.key}
            onClick={() => setLayers({ ...layers, [l.key]: !layers[l.key] })}
            className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition"
            style={{
              borderColor: layers[l.key] ? l.color : C.border,
              background: layers[l.key] ? `${l.color}10` : "white",
              color: layers[l.key] ? l.color : C.textMuted,
            }}
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: l.color, opacity: layers[l.key] ? 1 : 0.3 }} />
            {l.label}
            <span className="text-xs" style={{ opacity: 0.6 }}>({l.count})</span>
          </button>
        ))}
      </div>

      {/* ── Map ── */}
      <div className="relative overflow-hidden rounded-2xl border" style={{ borderColor: C.border }}>
        <div ref={mapRef} style={{ height: 600, width: "100%" }} />

        {/* Loading overlay */}
        {(catchLoading || schoolLoading || !mapReady) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <div className="flex items-center gap-3 text-sm" style={{ color: C.textMuted }}>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Loading catchment data…
            </div>
          </div>
        )}

        {/* DEMO badge */}
        <div className="absolute left-4 top-4 z-10">
          <span className="rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(217,119,6,0.9)", color: "white" }}>
            Demo Data
          </span>
        </div>

        {/* Zoom indicator */}
        <div className="absolute bottom-4 left-4 z-10 rounded-lg border bg-white/90 px-3 py-1.5 text-[10px] font-medium backdrop-blur" style={{ borderColor: C.border, color: C.textMuted }}>
          Zoom: {currentZoom} · {currentZoom < 9 ? "District clusters" : currentZoom < 12 ? "School markers" : "Detailed view"}
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-white p-3" style={{ borderColor: C.border }}>
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Legend</span>
        {financialMode === "financial" ? (
          <>
            <span className="text-xs" style={{ color: C.textMuted }}>{FINANCIAL_LAYERS.find(l => l.key === financialLayer)?.label}:</span>
            <div className="flex items-center gap-1.5"><span className="inline-block h-3 w-6 rounded" style={{ background: getFinancialColor(0.9, 1, financialLayer) }} /><span className="text-xs" style={{ color: C.text }}>High</span></div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-3 w-6 rounded" style={{ background: getFinancialColor(0.5, 1, financialLayer) }} /><span className="text-xs" style={{ color: C.text }}>Medium</span></div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-3 w-6 rounded" style={{ background: getFinancialColor(0.2, 1, financialLayer) }} /><span className="text-xs" style={{ color: C.text }}>Low</span></div>
          </>
        ) : (
          <>
            {Object.entries(SCHOOL_LEVEL_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: color }} />
                <span className="text-xs capitalize" style={{ color: C.text }}>{type}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-4 rounded" style={{ background: `${C.forest}20`, border: `1.5px solid ${C.forest}` }} />
              <span className="text-xs" style={{ color: C.text }}>Catchment area</span>
            </div>
          </>
        )}
      </div>

      {/* ── Catchment Integrity Dashboard ── */}
      <div className="rounded-xl border bg-white p-4" style={{ borderColor: C.border }}>
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" style={{ color: C.forest }} />
          <span className="text-sm font-semibold" style={{ color: C.text }}>Catchment Integrity</span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${C.amber}15`, color: C.amber }}>DEMO</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: C.text }}>{fmtNum(stats.activeCatchments)}</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Published</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: C.emerald }}>0</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Overlapping</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: C.emerald }}>0</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Invalid Geometry</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: stats.unassigned > 0 ? C.amber : C.emerald }}>{fmtNum(stats.unassigned)}</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Without Catchment</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: C.emerald }}>0</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Multi-Assigned</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: C.emerald }}>0</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Geographic Gaps</p>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {selCatchment && (
        <CatchmentModal
          catchment={selCatchment}
          schools={schools}
          onClose={() => setSelCatchment(null)}
          onSelectSchool={(s) => { setSelCatchment(null); setSelSchool(s); }}
          financialLayer={financialLayer}
        />
      )}
      {selSchool && (
        <SchoolModal
          school={selSchool}
          catchment={catchments.find(c => c.id === selSchool.catchment_id) ?? null}
          onClose={() => setSelSchool(null)}
          onBack={() => {
            const c = catchments.find(c => c.id === selSchool.catchment_id);
            setSelSchool(null);
            if (c) { setSelCatchment(c); fitBoundsToCatchment(c); }
          }}
        />
      )}
    </div>
  );
}
